import { useState, useEffect } from "react";
import { copyText } from "@/lib/clipboard";
import { useParams, useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth, API } from "@/App";
import axios from "axios";
import { 
  ArrowLeft, 
  MousePointerClick, 
  Globe, 
  Smartphone, 
  Monitor, 
  Tablet,
  ExternalLink,
  Copy,
  Check
} from "lucide-react";
import { toast } from "sonner";
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar
} from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ["#7c3aed", "#a855f7", "#c084fc", "#d8b4fe", "#e9d5ff"];

const AnalyticsPage = () => {
  const { linkId } = useParams();
  const navigate = useNavigate();
  const [link, setLink] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchData();
  }, [linkId]);

  const fetchData = async () => {
    try {
      const [linkRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/links/${linkId}`, { withCredentials: true }),
        axios.get(`${API}/links/${linkId}/analytics`, { withCredentials: true })
      ]);
      setLink(linkRes.data);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      toast.error("Gagal memuat data analytics");
      navigate("/links");
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = async () => {
 /*   const shortUrl = `${BACKEND_URL}/api/r/${link.short_code}`; */
    const shortUrl = `${window.location.origin}/${link.short_code}`;
    try {
      const ok = await copyText(shortUrl);
      if (!ok) throw new Error("copy failed");
      setCopied(true);
      toast.success("Link disalin!");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error("Gagal menyalin link");
    }
  };

  const getDeviceIcon = (device) => {
    switch (device.toLowerCase()) {
      case "mobile":
        return <Smartphone className="w-4 h-4" />;
      case "tablet":
        return <Tablet className="w-4 h-4" />;
      default:
        return <Monitor className="w-4 h-4" />;
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="analytics-page">
        {/* Header */}
        <div>
          <Button
            variant="ghost"
            className="mb-4"
            onClick={() => navigate("/links")}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Links
          </Button>
          
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ fontFamily: "Plus Jakarta Sans" }}>
                {link?.title || "Link Analytics"}
              </h1>
              <div className="flex items-center gap-2 mt-2">
                <p className="text-primary font-mono text-sm">
                  {window.location.origin}/{link?.short_code}
                </p>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={copyToClipboard}
                  data-testid="copy-link-btn"
                >
                  {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => window.open(`${window.location.origin}/${link?.short_code}`, "_blank")}
                  data-testid="open-link-btn"
                >
                  <ExternalLink className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="dashboard-card">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-primary/10 text-primary">
                  <MousePointerClick className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-2xl font-bold" style={{ fontFamily: "Plus Jakarta Sans" }}>
                    {analytics?.total_clicks || 0}
                  </p>
                  <p className="text-sm text-muted-foreground">Total Clicks</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="dashboard-card">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-green-500/10 text-green-500">
                  <Globe className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-2xl font-bold" style={{ fontFamily: "Plus Jakarta Sans" }}>
                    {analytics?.countries?.length || 0}
                  </p>
                  <p className="text-sm text-muted-foreground">Countries</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="dashboard-card">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-purple-500/10 text-purple-500">
                  <Monitor className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-2xl font-bold" style={{ fontFamily: "Plus Jakarta Sans" }}>
                    {analytics?.devices?.length || 0}
                  </p>
                  <p className="text-sm text-muted-foreground">Devices</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="dashboard-card">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-orange-500/10 text-orange-500">
                  <ExternalLink className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-2xl font-bold" style={{ fontFamily: "Plus Jakarta Sans" }}>
                    {analytics?.referrers?.length || 0}
                  </p>
                  <p className="text-sm text-muted-foreground">Referrers</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Clicks Over Time */}
          <Card className="dashboard-card">
            <CardHeader>
              <CardTitle className="text-lg">Clicks Over Time</CardTitle>
              <CardDescription>Performa klik per hari</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                {analytics?.clicks_by_date?.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={analytics.clicks_by_date}>
                      <XAxis 
                        dataKey="date" 
                        stroke="hsl(var(--muted-foreground))"
                        fontSize={12}
                        tickLine={false}
                        axisLine={false}
                      />
                      <YAxis 
                        stroke="hsl(var(--muted-foreground))"
                        fontSize={12}
                        tickLine={false}
                        axisLine={false}
                      />
                      <Tooltip 
                        contentStyle={{
                          background: "hsl(var(--card))",
                          border: "1px solid hsl(var(--border))",
                          borderRadius: "8px"
                        }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="clicks" 
                        stroke="hsl(var(--primary))"
                        strokeWidth={2}
                        dot={{ fill: "hsl(var(--primary))", strokeWidth: 2 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-muted-foreground">
                    Belum ada data klik
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Devices */}
          <Card className="dashboard-card">
            <CardHeader>
              <CardTitle className="text-lg">Devices</CardTitle>
              <CardDescription>Distribusi perangkat pengunjung</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                {analytics?.devices?.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={analytics.devices}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={5}
                        dataKey="value"
                        nameKey="name"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      >
                        {analytics.devices.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{
                          background: "hsl(var(--card))",
                          border: "1px solid hsl(var(--border))",
                          borderRadius: "8px"
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-muted-foreground">
                    Belum ada data perangkat
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Browsers */}
          <Card className="dashboard-card">
            <CardHeader>
              <CardTitle className="text-lg">Browsers</CardTitle>
              <CardDescription>Browser yang digunakan pengunjung</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                {analytics?.browsers?.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={analytics.browsers} layout="vertical">
                      <XAxis type="number" hide />
                      <YAxis 
                        type="category" 
                        dataKey="name" 
                        width={80}
                        stroke="hsl(var(--muted-foreground))"
                        fontSize={12}
                        tickLine={false}
                        axisLine={false}
                      />
                      <Tooltip 
                        contentStyle={{
                          background: "hsl(var(--card))",
                          border: "1px solid hsl(var(--border))",
                          borderRadius: "8px"
                        }}
                      />
                      <Bar dataKey="value" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-muted-foreground">
                    Belum ada data browser
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Top Referrers */}
          <Card className="dashboard-card">
            <CardHeader>
              <CardTitle className="text-lg">Top Referrers</CardTitle>
              <CardDescription>Sumber traffic terbanyak</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {analytics?.referrers?.length > 0 ? (
                  analytics.referrers.slice(0, 5).map((referrer, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center text-sm font-medium">
                          {index + 1}
                        </div>
                        <span className="text-sm truncate max-w-[200px]">{referrer.name}</span>
                      </div>
                      <span className="text-sm font-medium">{referrer.value}</span>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    Belum ada data referrer
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AnalyticsPage;
